import {
  domainRouterClient,
  type DomainRouterQueryArgs,
  type DomainRouterQueryData,
  type DomainRouterQueryName,
} from '../../api/domainRouterClient';

type GraphqlError = {
  message?: string;
};

type GraphqlResult<T> = {
  data: T | null;
  errors?: GraphqlError[] | null;
};

const toErrorMessage = (errors: GraphqlError[] | null | undefined): string =>
  (errors ?? [])
    .map((error) => String(error?.message ?? '').trim())
    .filter(Boolean)
    .join('; ');

export const unwrapGraphqlResult = <T>(operationId: string, result: GraphqlResult<T>): T => {
  const errorMessage = toErrorMessage(result.errors);
  if (errorMessage) {
    throw new Error(`${operationId} failed: ${errorMessage}`);
  }
  if (result.data == null) {
    throw new Error(`${operationId} returned no data`);
  }
  return result.data;
};

export const normalizeJsonValue = <T>(value: unknown, defaultValue: T): T => {
  if (value == null) {
    return defaultValue;
  }
  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) {
      return defaultValue;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  }
  return value as T;
};

export const asRecord = (value: unknown): Record<string, unknown> =>
  (value && typeof value === 'object' && !Array.isArray(value)) ? (value as Record<string, unknown>) : {};

export const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : []);

export const queryClient = () => domainRouterClient.queries();

export const runDomainRouterQuery = <Name extends DomainRouterQueryName>(
  name: Name,
  args: DomainRouterQueryArgs<Name>
): Promise<DomainRouterQueryData<Name>> => {
  return domainRouterClient.query(
    name as never,
    args as never,
  ) as Promise<DomainRouterQueryData<Name>>;
};
